import type {
  DashboardNotice,
  NormalizedNotice,
  NoticeChange,
  NoticeEvaluation,
  ReviewStatus,
  SyncRun,
  UserNotice,
} from "../notices/types";
import type {
  BrowserDatabase,
  KnownSettingKey,
  RemoteNoticeRecord,
  SettingRecord,
} from "./schema";

export interface UserNoticePatch {
  sourceNoticeIds?: string[];
  bookmarked?: boolean;
  reviewStatus?: ReviewStatus;
  assignee?: string;
  note?: string;
  hidden?: boolean;
  lastOpenedAt?: string | null;
}

export interface StorageManagerLike {
  persist?: () => Promise<boolean>;
  persisted?: () => Promise<boolean>;
  estimate?: () => Promise<{ usage?: number; quota?: number }>;
}

export interface StoragePersistenceResult {
  supported: boolean;
  persisted: boolean | null;
  requestedAt: string;
  error: string | null;
}

export interface BrowserStorageEstimate {
  supported: boolean;
  usage: number | null;
  quota: number | null;
  usageRatio: number | null;
}

const DEFAULT_REVIEW_STATUS: ReviewStatus = "미검토";

function nowIso(): string {
  return new Date().toISOString();
}

function uniqueStrings(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => value.length > 0))];
}

export function createUserNotice(
  noticeId: string,
  sourceNoticeIds: readonly string[] = [],
  timestamp = nowIso(),
): UserNotice {
  return {
    noticeId,
    sourceNoticeIds: uniqueStrings(sourceNoticeIds),
    bookmarked: false,
    reviewStatus: DEFAULT_REVIEW_STATUS,
    assignee: "",
    note: "",
    hidden: false,
    lastOpenedAt: null,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

export async function getUserNotice(
  database: BrowserDatabase,
  noticeId: string,
): Promise<UserNotice | null> {
  return (await database.userNotices.get(noticeId)) ?? null;
}

export async function listUserNotices(
  database: BrowserDatabase,
): Promise<UserNotice[]> {
  return database.userNotices.toArray();
}

export async function updateUserNotice(
  database: BrowserDatabase,
  noticeId: string,
  patch: UserNoticePatch,
  options: { sourceNoticeIds?: readonly string[]; now?: string } = {},
): Promise<UserNotice> {
  const timestamp = options.now ?? nowIso();

  return database.transaction("rw", database.userNotices, async () => {
    const current =
      (await database.userNotices.get(noticeId)) ??
      createUserNotice(noticeId, options.sourceNoticeIds, timestamp);
    const sourceNoticeIds = uniqueStrings([
      ...current.sourceNoticeIds,
      ...(options.sourceNoticeIds ?? []),
      ...(patch.sourceNoticeIds ?? []),
    ]);
    const next: UserNotice = {
      ...current,
      ...patch,
      noticeId,
      sourceNoticeIds,
      createdAt: current.createdAt,
      updatedAt: timestamp,
    };

    await database.userNotices.put(next);
    return next;
  });
}

export async function putUserNotice(
  database: BrowserDatabase,
  record: UserNotice,
): Promise<UserNotice> {
  const normalized: UserNotice = {
    ...record,
    sourceNoticeIds: uniqueStrings(record.sourceNoticeIds),
  };
  await database.userNotices.put(normalized);
  return normalized;
}

export async function deleteUserNotice(
  database: BrowserDatabase,
  noticeId: string,
): Promise<void> {
  await database.userNotices.delete(noticeId);
}

/**
 * Writes only the official notice store. The userNotices table is deliberately
 * absent from the transaction, so an upstream refresh cannot overwrite local
 * bookmarks, notes, review state, assignee, or hidden state.
 */
export async function upsertOfficialNotices(
  database: BrowserDatabase,
  notices: readonly RemoteNoticeRecord[],
): Promise<void> {
  await database.transaction("rw", database.remoteNotices, async () => {
    await database.remoteNotices.bulkPut([...notices]);
  });
}

export function toRemoteNoticeRecord(
  notice: NormalizedNotice,
  evaluation: NoticeEvaluation | null,
): RemoteNoticeRecord {
  const clientFields = notice as NormalizedNotice & {
    region?: unknown;
    sourceMissing?: unknown;
  };
  return {
    ...notice,
    region:
      typeof clientFields.region === "string" && clientFields.region.length > 0
        ? clientFields.region
        : deriveNoticeRegion(notice),
    sourceMissing: clientFields.sourceMissing === true,
    eligibilityStatus: evaluation?.eligibilityStatus ?? null,
    fitScore: evaluation?.fitScore ?? null,
    fitGrade: evaluation?.fitGrade ?? null,
  };
}

const REGION_NAMES = [
  "서울",
  "부산",
  "대구",
  "인천",
  "광주",
  "대전",
  "울산",
  "세종",
  "경기",
  "강원",
  "충북",
  "충남",
  "전북",
  "전남",
  "경북",
  "경남",
  "제주",
] as const;

export function deriveNoticeRegion(notice: NormalizedNotice): string {
  const searchable = [notice.title, notice.agency, notice.summary, notice.eligibilityText].join(" ");
  return REGION_NAMES.find((region) => searchable.includes(region)) ?? "전국";
}

export async function getSetting<T = unknown>(
  database: BrowserDatabase,
  key: KnownSettingKey | string,
): Promise<T | undefined> {
  const record = await database.settings.get(key);
  return record?.value as T | undefined;
}

export async function putSetting<T>(
  database: BrowserDatabase,
  key: KnownSettingKey | string,
  value: T,
  updatedAt = nowIso(),
): Promise<SettingRecord<T>> {
  const record: SettingRecord<T> = { key, value, updatedAt };
  await database.settings.put(record);
  return record;
}

export async function getLatestSyncRun(
  database: BrowserDatabase,
): Promise<SyncRun | null> {
  const runs = await database.syncRuns.toArray();
  runs.sort((left, right) => right.startedAt.localeCompare(left.startedAt));
  return runs[0] ?? null;
}

export async function getLastSuccessfulSyncRun(
  database: BrowserDatabase,
): Promise<SyncRun | null> {
  const runs = await database.syncRuns
    .filter((run) => run.status === "success")
    .toArray();
  runs.sort((left, right) => right.finishedAt.localeCompare(left.finishedAt));
  return runs[0] ?? null;
}

function calculateDday(endAt: string | null, now: Date): number | null {
  if (!endAt) return null;
  const end = new Date(endAt);
  if (Number.isNaN(end.getTime())) return null;
  // Product dates are Korean business dates. Shift both instants to KST before
  // reducing them to calendar days so 00:00-08:59 KST is not treated as the
  // previous UTC day.
  const toKoreanCalendarDay = (date: Date): number => {
    const korean = new Date(date.getTime() + 9 * 60 * 60 * 1_000);
    return Date.UTC(
      korean.getUTCFullYear(),
      korean.getUTCMonth(),
      korean.getUTCDate(),
    );
  };
  return Math.round((toKoreanCalendarDay(end) - toKoreanCalendarDay(now)) / 86_400_000);
}

export async function listDashboardNotices(
  database: BrowserDatabase,
  options: { includeHidden?: boolean; now?: Date } = {},
): Promise<DashboardNotice[]> {
  const [notices, evaluations, changes, users] = await Promise.all([
    database.remoteNotices.toArray(),
    database.remoteEvaluations.toArray(),
    database.remoteChanges.toArray(),
    database.userNotices.toArray(),
  ]);
  const evaluationByNoticeId = new Map(
    evaluations.map((evaluation) => [evaluation.noticeId, evaluation]),
  );
  const userByNoticeId = new Map(users.map((user) => [user.noticeId, user]));
  const changesByNoticeId = new Map<string, NoticeChange[]>();

  for (const change of changes) {
    const list = changesByNoticeId.get(change.noticeId) ?? [];
    list.push(change);
    changesByNoticeId.set(change.noticeId, list);
  }

  const currentDate = options.now ?? new Date();
  return notices
    .filter((notice) => options.includeHidden || !userByNoticeId.get(notice.id)?.hidden)
    .map((notice) => ({
      ...notice,
      evaluation: evaluationByNoticeId.get(notice.id) ?? null,
      user: userByNoticeId.get(notice.id) ?? null,
      changes: (changesByNoticeId.get(notice.id) ?? []).sort((left, right) =>
        right.detectedAt.localeCompare(left.detectedAt),
      ),
      dday: calculateDday(notice.applicationEndAt, currentDate),
    }));
}

function defaultStorageManager(): StorageManagerLike | undefined {
  return typeof navigator === "undefined" ? undefined : navigator.storage;
}

export async function requestPersistentStorage(
  database: BrowserDatabase,
  storageManager: StorageManagerLike | undefined = defaultStorageManager(),
  timestamp = nowIso(),
): Promise<StoragePersistenceResult> {
  let result: StoragePersistenceResult;

  if (!storageManager?.persist) {
    result = {
      supported: false,
      persisted: null,
      requestedAt: timestamp,
      error: null,
    };
  } else {
    try {
      const alreadyPersisted = storageManager.persisted
        ? await storageManager.persisted()
        : false;
      const persisted = alreadyPersisted || (await storageManager.persist());
      result = {
        supported: true,
        persisted,
        requestedAt: timestamp,
        error: null,
      };
    } catch (error) {
      result = {
        supported: true,
        persisted: false,
        requestedAt: timestamp,
        error: error instanceof Error ? error.message : "Storage persistence failed.",
      };
    }
  }

  await putSetting(database, "storagePersistence", result, timestamp);
  return result;
}

export async function estimateBrowserStorage(
  storageManager: StorageManagerLike | undefined = defaultStorageManager(),
): Promise<BrowserStorageEstimate> {
  if (!storageManager?.estimate) {
    return { supported: false, usage: null, quota: null, usageRatio: null };
  }

  const estimate = await storageManager.estimate();
  const usage = typeof estimate.usage === "number" ? estimate.usage : null;
  const quota = typeof estimate.quota === "number" ? estimate.quota : null;
  return {
    supported: true,
    usage,
    quota,
    usageRatio: usage !== null && quota && quota > 0 ? usage / quota : null,
  };
}
